import React from 'react';
import { View } from 'react-native-web';
import { connect } from 'react-redux';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import Janitor from '../../../../modules/Janitor';
import analytics from '../../../../modules/analytics';
import {
  IReview,
  loadRatingTagInfo,
  RatingTagInfo,
} from '../../../../modules/provider/reviews';
import ReviewResponseModal from '../../../provider/modals/ReviewResponseModal';
import { SnackbarContext, SnackbarContextType } from '../../SnackbarContext';
import ReviewList from './ReviewList';
import { getReviewPhotoSummary } from '../../../../modules/Reviews';
import { ReviewTagCollection } from './ReviewTagInfo';
import { urls as socialSharingUrls } from '../../../consumer/sharing/social/helpers';
import type { IRootDispatch, RootState } from '../../../../store/models';
import { ProviderReviewState } from '../../../../store/ProviderReviews.model';
import { IReviewPhotoSummary } from '../../../../types/ReviewTypes';
import { ProviderProfileForReviews } from '../../../../modules/provider/Profile';

type ConnectedReviewListContainerProps = {
  /** the profile object */
  profile: ProviderProfileForReviews;
  /** is in edit mode */
  isEditMode?: boolean;
  /** is in the showRatingsAndReviews page (route: responsive.provider.ratings_reviews) */
  showRatingsAndReviews?: boolean;
  showPinnedReview?: boolean;
  isInReviewTagFiltersTest?: boolean;
  /** AB Test DP-273 */
  isInReviewOrderingTest?: boolean;
  /** callback to update the proPinnedReview directive when pinning/unpinning reviews
   * on the reviews list
   */
  onPinReview?: (payload: IReview) => void;
};

type ReviewListValues = {
  showRatingsAndReviews: boolean;
  reviewCount: number;
  reviews: Array<IReview>;
  isLoading: boolean;
  reviewsHaveNext: boolean;
  showPinnedReview: boolean;
  currentPinnedReview?: IReview;
  reviewSummaryImages: IReviewPhotoSummary | null;
  emptyState: boolean;
  profile: ProviderProfileForReviews;
  isEditMode: boolean;
  allowFilter: boolean;
  isInReviewTagFiltersTest: boolean;
};

type ReviewListContainerProps = ConnectedReviewListContainerProps & {
  reviews?: Array<IReview>;
  isLoading: boolean;
  reviewCount?: number;
  reviewsHaveNext?: boolean;
  currentPinnedReview?: IReview;
  loadReviews: (payload: {
    providerId: number; showRatingsAndReviews?: boolean; search_term?: string;
  }) => Promise<void>;
  loadNextPage: (payload: {
    providerId: number; showRatingsAndReviews?: boolean; search_term?: string;
  }) => Promise<void>;
  pinReview: (payload: { providerId: number; review: IReview }) => Promise<IReview>;
  unpinReview: (payload: { review: IReview; providerId: number }) => Promise<IReview>;
  loadPinnedReview: (payload: { providerId: number }) => Promise<IReview>;
  reloadReview: (payload: { review: IReview; providerId: number }) => Promise<IReview>;

  // AB Test DP-273
  searchTerm?: string;
  isInReviewOrderingTest?: boolean;
};

type ReviewListContainerState = {
  ratingTagInfoArray: RatingTagInfo[];
  selectedTag: RatingTagInfo | null;
  reviewSummaryImages: IReviewPhotoSummary | null;
  isReviewResponseModalOpen: boolean;
  selectedReview: IReview;
  edit: boolean;
};

/**
 * Renders the review list container.
 *
 * @example -- React usage:
 * import ReviewListContainer from 'components/shared/providerProfile/reviews/ReviewListContainer'
 *
 * const MyComponent = () => {
 *   return (
 *     <ReviewListContainer profile={profile} />
 *   );
 * }
 */

export class ReviewListContainerBase extends React.Component<ReviewListContainerProps,
ReviewListContainerState> {
  static defaultProps = {
    isEditMode: false,
    showRatingsAndReviews: false,
    onPinReview: () => { },
    currentPinnedReview: null,
    reviews: [],
    reviewCount: 0,
    reviewsHaveNext: false,
    showPinnedReview: false,
    isInReviewTagFiltersTest: false,
    searchTerm: '',
    isInReviewOrderingTest: false,
  };

  static contextType = SnackbarContext;

  state = {
    reviewSummaryImages: {
      results: [],
      count: 0,
    },
    ratingTagInfoArray: [],
    selectedTag: null,
    isReviewResponseModalOpen: false,
    selectedReview: null,
    edit: false,
  };

  janitor = new Janitor();

  componentDidMount() {
    this.getRatingTagInfo();
    this.janitor.addPromise(this.loadReviews());
    this.loadReviewSummaryImages();
  }

  componentDidUpdate(prevProps) {
    const { currentPinnedReview, onPinReview } = this.props;
    if (prevProps.currentPinnedReview !== currentPinnedReview) {
      onPinReview(currentPinnedReview);
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  isOrderReviewAbTest = () => {
    const {
      isInReviewOrderingTest,
      searchTerm,
    } = this.props;
    return isInReviewOrderingTest && searchTerm?.length > 0;
  };

  /**
   ATL-3979
   */
  getRatingTagInfo = async () => {
    const {
      profile: {
        id,
      },
    } = this.props;
    const ratingTagInfoArray = await loadRatingTagInfo(id);
    this.setState({
      ratingTagInfoArray,
    });
  };

  /**
   * Makes the api call to fetch the first three review photos from all
   * the reviews even if they are on future pages.
   */
  loadReviewSummaryImages = async () => {
    const {
      profile: {
        id,
      },
    } = this.props;
    const reviewSummaryImages = await this.janitor.addPromise(
      getReviewPhotoSummary(id),
    );

    this.setState({
      reviewSummaryImages,
    });
  };

  /**
   * Makes the API call to fetch reviews
   */
  loadReviews = () => {
    const {
      profile,
      showRatingsAndReviews,
      loadReviews,
      loadPinnedReview,
      searchTerm,
    } = this.props;

    loadPinnedReview({ providerId: profile.id });

    if (this.isOrderReviewAbTest()) {
      analytics.track('view', {
        component: 'ReviewListContainerBase',
        providerId: profile.id,
        searchTerm,
        abTest: 'review-ordering-test',
      });
    }

    return loadReviews({
      providerId: profile.id,
      showRatingsAndReviews,
      search_term: this.isOrderReviewAbTest() ? searchTerm : undefined,
    });
  };

  loadNextPage = () => {
    const {
      profile,
      showRatingsAndReviews,
      loadNextPage,
      searchTerm,
    } = this.props;

    if (this.isOrderReviewAbTest()) {
      analytics.track('view', {
        component: 'ReviewListContainerBase',
        providerId: profile.id,
        searchTerm,
        abTest: 'review-ordering-test',
      });
    }

    return loadNextPage({
      providerId: profile.id,
      showRatingsAndReviews,
      search_term: this.isOrderReviewAbTest() ? searchTerm : undefined,
    });
  };

  /**
   * Load more reviews on button click
   */
  handleShowMoreClick = () => {
    const { isLoading } = this.props;

    if (!isLoading) {
      this.janitor.addPromise(this.loadNextPage());
    }
  };

  /**
   * Updates the ui for the targeted review (e.g. pinning/unpinning and responding to review)
   * @param {object} targetedReview - the targeted review to update
   */
  reloadReview = targetedReview => {
    const { profile, reloadReview } = this.props;

    const queryParams = {
      providerId: profile.id,
      review: targetedReview,
    };

    reloadReview(queryParams);
  };

  /**
   * handles the response modal to respond to client reviews
   * @param {object} review - the review object
   * @param {boolean} edit - if editing an existing response
   */
  openResponseModal = (review: IReview, edit: boolean) => {
    analytics.track('P Clicked Reply Review');

    this.setState({
      isReviewResponseModalOpen: true,
      selectedReview: review,
      edit,
    });
  };

  /**
   * handles the pinning/unpinning of reviews
   * @param {object} review - the review object
   */
  handlePinningReview = review => {
    const { profile } = this.props;

    if (review.pinned) {
      this.unPinReview(review);
    } else {
      this.pinReview(review, profile.id);
    }
  };

  /**
   * handles unpinning of a review
   * @param {object} review - the review object
   */
  unPinReview = review => {
    const { open: openSnackbar } = (this.context as SnackbarContextType);
    const {
      profile, unpinReview,
    } = this.props;
    // Review to unpin
    const unpinParams = {
      providerId: profile.id,
      review,
    };

    unpinReview(unpinParams).then(() => {
      openSnackbar({
        icon: 'success-icon',
        sideColor: BrandColor.Green,
        inlineHighlight: 'The review has been unpinned.',
        timeOut: 5000,
      });
    });
  };

  /**
   * handles pinning of a review
   * @param {object} review - the review object
   * @param {object} providerId The ID of the provider associated with the review
   */
  pinReview = (review, providerId) => {
    const { open: openSnackbar } = (this.context as SnackbarContextType);
    const {
      pinReview,
    } = this.props;

    pinReview({ review, providerId }).then(() => {
      analytics.track('P Clicked Highlight Review');
      openSnackbar({
        icon: 'success-icon',
        sideColor: BrandColor.Green,
        inlineHighlight: 'The review has been pinned.',
        timeOut: 5000,
      });
    });
  };

  /**
   * Get the facebook review share url
   */
  getFacebookReviewShareUrl = review => {
    const { profile } = this.props;
    return socialSharingUrls.review(profile, review.id).facebook;
  };

  /**
   * tracks when fb share button clicked
   */
  trackShare = review => { // eslint-disable-line
    analytics.track('P Clicked Share Review FB', { review_id: review.id });
  };

  /**
   * Handles pressing of individual review images
   */
  handleImageSelected = ({
    id,
    imageCount,
  }) => {
    analytics.track('client_review_photo_thumbnail_clicked', {
      photo_id: id,
      num_photos: imageCount,
      provider_id: this.props.profile.id,
    });
  };

  /**
   * Handles when the review all image gallery is opened
   */
  handleImageGalleryOpen = ({
    id,
    imageCount,
  }) => {
    analytics.track('client_review_photo_gallery_clicked', {
      photo_id: id,
      num_photos: imageCount,
      num_reviews: this.props.profile.ratings_count,
      provider_id: this.props.profile.id,
    });
  };

  /**
   * Handles when an image is shown in the gallery
   * @returns {*}
   */
  handleImageShown = ({
    id,
    imageCount,
    imageIndex,
  }) => {
    analytics.track('client_review_photo_viewed', {
      photo_id: id,
      num_photos: imageCount,
      gallery_photo_id: imageIndex,
      provider_id: this.props.profile.id,
    });
  };

  handleTagPress = tag => {
    this.setState({ selectedTag: tag });
  };

  filterReviews(reviewsData) {
    const { selectedTag } = this.state;
    if (!selectedTag) {
      return reviewsData;
    }
    const {
      group, value, count,
    } = selectedTag;

    const reviewDataFiltered = reviewsData.filter(review => {
      const reviewTagArray = review[group];
      return reviewTagArray.includes(value);
    });

    if (reviewDataFiltered.length === 0 && count > 0) {
      this.handleShowMoreClick();
    }
    return reviewDataFiltered;
  }

  renderReviewList(reviewListProps: ReviewListValues) {
    const { selectedTag, ratingTagInfoArray } = this.state;

    const {
      reviews,
      showRatingsAndReviews,
      reviewCount,
      isLoading,
      reviewsHaveNext,
      showPinnedReview,
      currentPinnedReview,
      reviewSummaryImages,
      emptyState,
      profile,
      isEditMode,
      allowFilter,
      isInReviewTagFiltersTest,
    } = reviewListProps;
    const filteredReviews = allowFilter ? this.filterReviews(reviews) : reviews;
    const isEndOfFilteredReviews = (allowFilter
      && selectedTag
      && selectedTag.count === filteredReviews.length
    );

    return (
      <ReviewList
        showHeaderContent={!allowFilter}
        isEndOfFilteredReviews={isEndOfFilteredReviews}
        reviews={filteredReviews}
        reviewCount={reviewCount}
        providerId={profile.id}
        isLoading={isLoading}
        reviewsHaveNext={reviewsHaveNext}
        standalonePage={showRatingsAndReviews}
        isProvider={isEditMode}
        provider={profile}
        avgRating={profile.rating_stars || 0}
        onShowMoreClick={this.handleShowMoreClick}
        onPinReview={this.handlePinningReview}
        getFacebookReviewShareUrl={this.getFacebookReviewShareUrl}
        trackShare={this.trackShare}
        openResponseModal={this.openResponseModal}
        isEmpty={emptyState}
        onReviewImageSelected={this.handleImageSelected}
        onReviewImageShown={this.handleImageShown}
        reviewSummaryImages={reviewSummaryImages}
        onReviewImageGalleryOpened={this.handleImageGalleryOpen}
        showPinnedReview={showPinnedReview}
        pinnedReview={allowFilter ? null : currentPinnedReview}
        isInReviewTagFiltersTest={isInReviewTagFiltersTest && ratingTagInfoArray.length > 0}
        serviceId={undefined}
        serviceName={undefined}
        hideSummary={false}
        highlightedReview={null}
      />
    );
  }

  render() {
    const {
      profile,
      isEditMode,
      showRatingsAndReviews,
      reviewCount,
      reviews,
      isLoading,
      reviewsHaveNext,
      showPinnedReview,
      currentPinnedReview,
      isInReviewTagFiltersTest,
    } = this.props;

    const {
      reviewSummaryImages,
      ratingTagInfoArray,
      isReviewResponseModalOpen,
      selectedReview,
      edit,
    } = this.state;

    const emptyState = isEditMode && !profile.rating_stars;

    const reviewListProps = {
      showRatingsAndReviews,
      reviewCount,
      reviews,
      isLoading,
      reviewsHaveNext,
      showPinnedReview,
      currentPinnedReview,
      reviewSummaryImages,
      emptyState,
      profile,
      isEditMode,
      allowFilter: false,
      isInReviewTagFiltersTest,
    };

    return (
      <React.Fragment>

        <View nativeID="review-list-container">
          <Box>
            {isInReviewTagFiltersTest && ratingTagInfoArray.length > 0 && (
              <ReviewTagCollection
                reviewCount={profile.ratings_count}
                avgRating={profile.rating_stars || 0}
                providerId={profile.id}
                ratingTagInfoArray={ratingTagInfoArray}
                renderReviewList={() => this.renderReviewList(
                  { ...reviewListProps, allowFilter: true },
                )}
                onTagPress={this.handleTagPress}
              />
            )}
            {this.renderReviewList(reviewListProps)}
          </Box>
        </View>
        {isReviewResponseModalOpen ? (
          <ReviewResponseModal
            review={selectedReview}
            edit={edit}
            proId={profile.id}
            refreshReviews={this.reloadReview}
            onClose={() => this.setState({
              isReviewResponseModalOpen: false,
              selectedReview: null,
            })}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: RootState, { profile }) => {
  const { providerReviews: { loading, providerMap }, search } = state;
  const providerRecord: ProviderReviewState = providerMap[profile?.id] || {
    reviews: [],
    total: 0,
    hasNextPage: false,
    latestPage: 0,
  };

  return {
    reviews: providerRecord.reviews,
    isLoading: loading,
    reviewCount: providerRecord.total,
    reviewsHaveNext: providerRecord.hasNextPage,
    currentPinnedReview: providerRecord.pinnedReview,
    searchTerm: search.query,
  };
};

const mapDispatchToProps = ({ providerReviews }: IRootDispatch) => ({
  loadReviews: providerReviews.loadFirstPage,
  loadNextPage: providerReviews.loadNextPage,
  pinReview: providerReviews.pinReview,
  unpinReview: providerReviews.unpinReview,
  reloadReview: providerReviews.reloadReview,
  loadPinnedReview: providerReviews.loadPinnedReview,
});

export default connect(mapStateToProps, mapDispatchToProps)(
  ReviewListContainerBase,
) as React.ComponentType<ConnectedReviewListContainerProps>;
